import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"

import css from "./Overlay.scss"
import { getAssetPath, sanitizer } from "@commonUtils"

import ImageLoader from "@components/ImageLoader/ImageLoader"
import { isMobileBrowser } from "@commonUtils"

function fixSafariIOSBgScroll(isOverlayOpen) {
    // https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open
    const body = document.body
    if (isOverlayOpen) {
        const scrollY = `${window.scrollY}px`
        body.style.position = "fixed"
        body.style.inset = `-${scrollY} 0 0 0`
    } else {
        if (body.style.position === "fixed") {
            // Undo the body styles and scroll the window to current position (Otherwise it just scrolls to top)
            const scrollY = body.style.top
            body.style.position = ""
            body.style.inset = ""
            // Set scroll-behavior of html element to "auto" (to avoid any scroll animation)
            const scrollingElement = document.documentElement
            scrollingElement.style.scrollBehavior = "auto"
            window.scrollTo(0, parseInt(scrollY || "0") * -1)
            scrollingElement.style.scrollBehavior = ""
        }
    }
}

function Overlay(props) {
    const scrollableDivRef = useRef(null)
    const containerType =
        props.direction === "bottom" || props.direction === "top"
            ? css.containerVertical
            : css.containerHorizontal
    let poppingDirectionCss = css.popFromBottom
    let closingDirectionCss = css.containerClosedBottom

    useEffect(() => {
        return () => {
            document.body.style.overflow = ""
            if (isMobileBrowser() === "iOS") {
                fixSafariIOSBgScroll(false)
            }
        }
    }, [])

    useEffect(() => {
        if (props.show && !props.maintainScroll && scrollableDivRef?.current) {
            /* On opening of overlay, scroll to top */
            scrollableDivRef.current.scrollTop = 0
        }
        document.body.style.overflow = props.show ? "hidden" : ""
        // Setting overflow="hidden" doesn't stop background scroll on IOS Safari. Some extra stuff is needed to handle this.
        if (isMobileBrowser() === "iOS") {
            fixSafariIOSBgScroll(props.show)
        }
    }, [props.show])

    if (props.direction === "top") {
        poppingDirectionCss = css.popFromTop
        closingDirectionCss = css.containerClosedTop
    } else if (props.direction === "left") {
        poppingDirectionCss = css.popFromLeft
        closingDirectionCss = css.containerClosedLeft
    } else if (props.direction === "right") {
        poppingDirectionCss = css.popFromRight
        closingDirectionCss = css.containerClosedRight
    }

    return (
        <>
            <div
                className={`${css.overlayTransition} ${props.show ? css.overlay : css.hidden}`}
                onClick={() => props.toggle(!props.show)}
            />
            <div
                className={`flex alignCenter justifyCenter ${containerType} ${
                    props.direction === "top" ? css.reverseFlexFlow : ""
                } ${css[props.size]} ${props.show ? css.containerOpen : closingDirectionCss}
                    ${poppingDirectionCss}
                    ${props.isFixed ? css.fixed : ""}`}
                style={props.style || {}}
            >
                <div
                    className={`${css.bsContent} ${props.isFixed ? css.fixedContent : ""}`}
                    ref={scrollableDivRef}
                >
                    {/* <div className={`${css.bg}`}>
                        <div
                            style={{
                                borderRadius: 20,
                                padding: 10,
                            }}
                        >
                            <ImageLoader
                                height="24px"
                                width="24px"
                                alt={"close sheet"}
                                className={css.closeButton}
                                src={getAssetPath("svgs/icons/cross_24px_primary.svg")}
                                onClick={() => props.toggle(!props.show)}
                            />
                        </div>
                    </div> */}
                    <div>
                        {(props.showBackIcon || props.showCloseIcon || props.header) && (
                            <div className={`container-fluid ${css.header}`}>
                                <div className={`flex alignCenter gutter-only ${css.headerContent}`}>
                                    {props.showBackIcon && (
                                        <ImageLoader
                                            height="24px"
                                            width="24px"
                                            alt={"go back"}
                                            className={css.backButton}
                                            onClick={() => {
                                                if (props.backHandler) {
                                                    // if your bottomsheet information is changing by clicking back icon ex - offer bottomsheet on pdp
                                                    props.backHandler()
                                                } else {
                                                    props.toggle(!props.show)
                                                }
                                            }}
                                            src={getAssetPath("svgs/icons/arrow-left_24px_primary.svg")}
                                        />
                                    )}
                                    {props.header && (
                                        <div
                                            className={`l4SemiBold ${css.bold} ${css.clipText} ${css.htmlNodeWrapper}`}
                                            // Setting this dangerously in order to handle HTML tags for bold
                                            dangerouslySetInnerHTML={{ __html: sanitizer(props.header) }}
                                        />
                                    )}
                                    {props.showCloseIcon && (
                                        <ImageLoader
                                            height="24px"
                                            width="24px"
                                            alt={"close sheet"}
                                            className={css.closeButton}
                                            src={getAssetPath("svgs/icons/cross_24px_primary.svg")}
                                            onClick={() => props.toggle(!props.show)}
                                        />
                                    )}
                                </div>
                            </div>
                        )}
                        {props.children}
                    </div>
                </div>
            </div>
        </>
    )
}

Overlay.propTypes = {
    maintainScroll: PropTypes.bool,
    show: PropTypes.bool,
    showCloseIcon: PropTypes.bool,
    showBackIcon: PropTypes.bool,
    toggle: PropTypes.func.isRequired,
    children: PropTypes.instanceOf(Object).isRequired,
    size: PropTypes.string,
    direction: PropTypes.oneOf(["top", "bottom", "left", "right"]),
    isFixed: PropTypes.bool,
    hasSeparator: PropTypes.bool,
    header: PropTypes.string,
    style: PropTypes.object,
    backHandler: PropTypes.func,
}

Overlay.defaultProps = {
    maintainScroll: true,
    show: false,
    size: "medium",
    showCloseIcon: true,
    showBackIcon: false,
    direction: "bottom",
    isFixed: false,
    hasSeparator: false,
}

export default Overlay
